// utils/itemTags.js
//
// THE TAGS ON ONE ITEM, the client's pure half (docs/plans/tags.md §3.3,
// §5.1, §5.2; keepr-api docs/SCHEMA.md § Tags on items, § Your private tags
// on items, § Item tags). What an item read carries, and how a surface turns
// it into chips, removals, writes and the sentences a refusal is said in.
// No React, no network: the chip row (components/tags/ItemTagChips), the
// picker (components/tags/TagSelect), the writer (hooks/useItemTagWriter),
// the form, the table, the tiles, Filter by… and the selection bar all read
// these, so none of them re-states a rule.
//
// An item read (GET /api/items, GET /api/items/{id}, POST and PUT answers)
// carries, beside its elements:
//
//   tagIds      hand applications (people write it; ≤ 50; absent when empty)
//   tagAutoIds  a rule's applications (server-managed; never sent back)
//   tagTitles   { <tagId>: { kind: 'tag', name, color, icon, restricted,
//                            openable? }                             a collection tag
//                        | { title, item_id, card_id }                      an item tag
//                        | { unavailable: true } }                          one the reader can't read
//   myTags      [{ tagId, kind: 'tag' | 'item', name?, fromCollection? }]  the reader's private applications
//
// A collection tag with NO `tagTitles` entry is one whose collection the
// reader cannot see of their own (a global collection's consumer, a linked
// reader — the API's closed default, owner call B2): the chip is NEUTRAL,
// never a guess at a name. One whose entry says `openable: false` is named on
// this row but is not the reader's to open (keepr-api b74: a restricted tag
// to someone who reads only some of the collection's items, or only a
// sub-collection; any tag to a global or linked reader) — its page would be
// the one 404, so the chip is no door (utils/tags.js tagPagePath).

import { normalizeId } from './ids';
import { isRestrictedTag, tagIdOf, tagOwnerCollectionId } from './tags';
import { ancestorsOf, managesParentOf, subCollectionsOf } from './collectionFamily';
import { canManage as canManageCollection } from './permissions';
import { ACCOUNT_LINK_FIELD_WORDS } from './apiErrors';

/** The ruled caps (d05; keepr-api tagValues / tagApplicationUtils), for the sentences. */
export const ITEM_TAG_LIMITS = Object.freeze({
	perItem: 50,
	privateItems: 2000,
	privateCollections: 50,
});

/** A collection tag the reader can see is applied, but not named (B2). */
export const NEUTRAL_TAG_LABEL = 'Tag';
export const NEUTRAL_TAG_NOTE = 'This tag belongs to a collection you can’t see.';

const idList = value => (Array.isArray(value) ? value : [])
	.map(normalizeId)
	.filter(Boolean);

/** The hand-applied tag ids, in stored order. */
export const handTagIdsOf = item => idList(item?.tagIds);
/** The ids a rule applied. */
export const autoTagIdsOf = item => idList(item?.tagAutoIds);
/** The reader's own private applications ON the item (not the ones its collection carries). */
export const privateApplicationsOf = item => (Array.isArray(item?.myTags) ? item.myTags : [])
	.filter(entry => entry && normalizeId(entry.tagId) && !entry.fromCollection);
/** The private tags the item's collection carries, for this reader ("From the collection"). */
export const inheritedApplicationsOf = item => (Array.isArray(item?.myTags) ? item.myTags : [])
	.filter(entry => entry && normalizeId(entry.tagId) && entry.fromCollection);

/** Does the item carry any tag this reader would see? */
export const hasAnyTag = item => handTagIdsOf(item).length > 0
	|| autoTagIdsOf(item).length > 0
	|| (Array.isArray(item?.myTags) && item.myTags.length > 0);

const asMap = source => {
	if (source instanceof Map) return source;
	const map = new Map();
	(Array.isArray(source) ? source : []).forEach(tag => {
		const id = tagIdOf(tag);
		if (id) map.set(id, tag);
	});
	return map;
};

const titleEntry = (item, titles, id) => {
	const own = item?.tagTitles && typeof item.tagTitles === 'object' ? item.tagTitles[id] : undefined;
	if (own) return own;
	if (titles instanceof Map) return titles.get(id);
	return titles && typeof titles === 'object' ? titles[id] : undefined;
};

/** An item tag as the chip reads it: `{ _id, kind: 'item', title, item_id, card_id }`. */
export const itemTagRecord = (id, entry) => ({
	_id: id,
	kind: 'item',
	title: typeof entry?.title === 'string' ? entry.title : '',
	item_id: normalizeId(entry?.item_id) || null,
	card_id: normalizeId(entry?.card_id) || null,
	...(entry?.collection_id ? { collection_id: normalizeId(entry.collection_id) } : {}),
});

/**
 * What one applied id reads as, for this reader: the vocabulary's tag (the
 * freshest name this session holds), else the item's own `tagTitles` entry,
 * else — an item tag it cannot read, or one turned off — a tombstone, else a
 * neutral chip. `{ tag, source, tombstone, neutral }`.
 */
export function resolveAppliedTag(id, { item = null, vocabulary = null, titles = null } = {}) {
	const vocab = asMap(vocabulary).get(id);
	if (vocab) return { tag: vocab, source: 'collection', tombstone: false, neutral: false };
	const entry = titleEntry(item, titles, id);
	if (entry?.unavailable) return { tag: null, source: 'item', tombstone: true, neutral: false };
	if (entry && (typeof entry.title === 'string' || entry.item_id)) {
		return { tag: itemTagRecord(id, entry), source: 'item', tombstone: false, neutral: false };
	}
	if (entry && (entry.kind === 'tag' || typeof entry.name === 'string')) {
		return {
			tag: {
				_id: id,
				v: 2,
				kind: 'tag',
				owner: { kind: 'collection', collection_id: normalizeId(entry.collection_id) || null },
				name: entry.name || NEUTRAL_TAG_LABEL,
				color: entry.color || null,
				icon: entry.icon || null,
				restricted: Boolean(entry.restricted),
				...(entry.openable === false ? { openable: false } : {}),
			},
			source: 'collection',
			tombstone: false,
			neutral: false,
		};
	}
	return {
		tag: { _id: id, v: 2, kind: 'tag', owner: { kind: 'collection', collection_id: null }, name: NEUTRAL_TAG_LABEL, neutral: true },
		source: 'collection',
		tombstone: false,
		neutral: true,
	};
}

/**
 * Every tag chip the item shows this reader, in order: its collection and
 * item tags (hand-applied ones in stored order, then a rule's own), then the
 * reader's private tags on it, then the private tags its collection carries
 * ("From the collection"). One entry per APPLICATION kind, so an item tag
 * the reader applied privately beside a hand application shows twice — each
 * chip's ✕ takes off its own.
 *
 *   { key, id, tag, source: 'collection' | 'item' | 'private',
 *     hand, automatic, private, inherited, tombstone, neutral, restricted }
 *
 * `vocabulary` — the item's collection's tags (useCollectionTags), an array
 * or an id → tag Map; `privateTags` — my private tags (TagsContext), for
 * their color and icon (myTags carries only the name); `titles` — extra
 * item-tag titles the surface holds (a picker's search, the form's picks).
 * `activeTagId` — a NEW item's form only: my active tag, whose chip is marked
 * `active` ("Your active tag", docs/plans/tags-active-tag-proposal.md).
 */
export function itemTagEntries(item, { vocabulary = null, privateTags = null, titles = null, activeTagId = null } = {}) {
	if (!item) return [];
	const activeId = normalizeId(activeTagId) || null;
	const vocab = asMap(vocabulary);
	const mine = asMap(privateTags);
	const hand = handTagIdsOf(item);
	const auto = autoTagIdsOf(item);
	const handSet = new Set(hand);
	const autoSet = new Set(auto);
	const out = [];
	[...hand, ...auto.filter(id => !handSet.has(id))].forEach(id => {
		const resolved = resolveAppliedTag(id, { item, vocabulary: vocab, titles });
		out.push({
			key: `c:${id}`,
			id,
			tag: resolved.tag,
			source: resolved.source,
			hand: handSet.has(id),
			automatic: autoSet.has(id),
			private: false,
			inherited: false,
			tombstone: resolved.tombstone,
			neutral: resolved.neutral,
			restricted: !resolved.tombstone && isRestrictedTag(resolved.tag),
			active: false,
		});
	});
	const privateEntry = (application, inherited) => {
		const id = normalizeId(application.tagId);
		let tag = null;
		let tombstone = false;
		let source = 'private';
		if (application.kind === 'item') {
			const resolved = resolveAppliedTag(id, { item, vocabulary: null, titles });
			tag = resolved.neutral ? null : resolved.tag;
			tombstone = resolved.tombstone || resolved.neutral;
			source = 'item';
		} else {
			const known = mine.get(id);
			tag = known || { _id: id, v: 2, kind: 'tag', owner: { kind: 'user' }, name: application.name || '' };
			if (!tag.name && application.name) tag = { ...tag, name: application.name };
		}
		return {
			key: `${inherited ? 'i' : 'p'}:${id}`,
			id,
			tag,
			source,
			hand: false,
			automatic: false,
			private: true,
			inherited,
			tombstone,
			neutral: false,
			restricted: false,
			// The active tag is a private TAG the person put on this form's
			// new item — never one the collection carries, never an item tag.
			active: Boolean(activeId) && !inherited && application.kind !== 'item' && id === activeId,
		};
	};
	privateApplicationsOf(item).forEach(application => out.push(privateEntry(application, false)));
	inheritedApplicationsOf(item).forEach(application => out.push(privateEntry(application, true)));
	return out;
}

/**
 * THE TAGS A PUBLIC PAGE SHOWS (T7; docs/plans/tags.md §3.5, ruled d24). A
 * public link's item and list pages (`/api/share/{code}`, `…/items`) serve,
 * on each item, only the tags they disclose, each with its `tagTitles` entry:
 * a collection tag that is NOT restricted, and an item tag whose item the page
 * could publish. So the page draws exactly those, read-only — and draws
 * NOTHING for an id without an entry (never the neutral "Tag" chip, which
 * would say a tag is there that the page does not disclose), nothing
 * restricted, nothing private, no tombstone. No vocabulary is asked: a
 * public visitor has no session.
 */
export function publicTagEntries(item) {
	// No vocabulary: every name is the item's own `tagTitles` entry, and an
	// id without one resolves neutral (dropped here), an unreadable one to a
	// tombstone (dropped too).
	return itemTagEntries(item).filter(entry => !entry.private
		&& !entry.neutral
		&& !entry.tombstone
		&& !entry.restricted);
}

/** The words a chip reads as (for Filter by…, a tooltip, a sentence). */
export function entryName(entry) {
	if (!entry || entry.tombstone || !entry.tag) return '';
	if (entry.tag.kind === 'item') return entry.tag.title || '';
	return entry.tag.name || '';
}

// ------------------------------------------------------------------ who may

const collectionIdOf = collection => normalizeId(collection?._id) || '';

/**
 * Does the reader manage `ownerId` — the collection a restricted tag
 * belongs to? Read off what the list's collection says about itself: its
 * own access (a manager of a collection manages its sub-collections; grants
 * flow down), or its parent's (managesParentOf). Anything else: no — the
 * API is the authority and says so (403 restricted_tag).
 */
export function managesTagOwner(collection, ownerId) {
	const owner = normalizeId(ownerId);
	if (!owner || !collection) return false;
	const self = collectionIdOf(collection);
	if (owner === self) return canManageCollection(collection);
	if (subCollectionsOf(collection).some(sub => sub._id === owner)) return canManageCollection(collection);
	// A tag of the collection above: its managers are the ones whose access
	// here comes from it (utils/collectionFamily managesParentOf).
	if (ancestorsOf(collection).some(ancestor => ancestor._id === owner)) return managesParentOf(collection);
	return false;
}

/**
 * May the reader take this chip off? (docs/plans/tags.md §5.1)
 *
 * - "From the collection": never here (it is on the collection).
 * - A private tag, or a private use of an item tag: yes — read is enough.
 * - Applied by a rule alone: no ✕ (the rule owns it).
 * - By hand: write on the item; a RESTRICTED tag only for managers of the
 *   collection it belongs to. With a rule's application too, the ✕ takes off
 *   the hand one only, and the chip stays while the rule holds.
 */
export function canRemoveEntry(entry, { canEdit = false, collection = null } = {}) {
	if (!entry || entry.inherited) return false;
	if (entry.private) return true;
	if (!entry.hand) return false;
	if (!canEdit) return false;
	if (entry.restricted) return managesTagOwner(collection, tagOwnerCollectionId(entry.tag));
	return true;
}

/** The ✕'s accessible name. */
export function removeLabelFor(entry) {
	const name = entry?.tombstone ? 'this tag' : (entryName(entry) || 'this tag');
	if (entry?.hand && entry?.automatic) return `Remove ${name} (the hand-applied one; the rule keeps its own)`;
	// The active tag: off this one item, still on for the next.
	if (entry?.active) return `Leave ${name} off this item`;
	return `Remove ${name}`;
}

/**
 * May the reader put this tag on by hand (into `tagIds`)? `{ ok, reason }`.
 * The API judges again (utils/tagValues.js); these are the reasons it would
 * refuse that this side can know, said before anyone clicks.
 */
export function canApplyByHand(tag, { canEdit = false, collection = null } = {}) {
	if (!canEdit) return { ok: false, reason: 'You can read this item but not change it. Private tags still work.' };
	if (tag?.rule?.strict) return { ok: false, reason: 'A rule applies this tag, so it can’t be put on by hand.' };
	if (isRestrictedTag(tag) && !managesTagOwner(collection, tagOwnerCollectionId(tag))) {
		return { ok: false, reason: 'Restricted: only managers put it on items.' };
	}
	return { ok: true, reason: null };
}

// ------------------------------------------------------------------ family

/**
 * The collections whose ITEM TAGS may go on an item of `itemCollectionId` by
 * hand: the item's own collection and every collection above it (keepr-api
 * § Tags on items: an item tag is carried inside its item's family — the
 * tagged item lives in the tag's item's collection or beneath it). Read off
 * the list's collection (`collection`) — its own id, its ancestors, its
 * sub-collections — never a request of its own.
 */
export function carrierCollectionIds(itemCollectionId, collection) {
	const out = new Set();
	const target = normalizeId(itemCollectionId) || collectionIdOf(collection);
	if (!target) return out;
	out.add(target);
	const self = collectionIdOf(collection);
	const chain = ancestorsOf(collection).map(ancestor => ancestor._id);
	if (target === self) {
		chain.forEach(id => out.add(id));
		return out;
	}
	if (subCollectionsOf(collection).some(sub => sub._id === target)) {
		out.add(self);
		chain.forEach(id => out.add(id));
		return out;
	}
	// The item lives above the list's collection (a parent's record read by
	// reference): its own chain is the part of ours above it.
	const at = chain.indexOf(target);
	if (at >= 0) chain.slice(0, at).forEach(id => out.add(id));
	return out;
}

// ------------------------------------------------------------------ writes

/** `list` with `add` appended (once each) and `remove` taken out, stored order kept. */
export function applyTagDelta(list = [], { add = [], remove = [] } = {}) {
	const drop = new Set(idList(remove));
	const out = [];
	const seen = new Set();
	[...idList(list), ...idList(add)].forEach(id => {
		if (drop.has(id) || seen.has(id)) return;
		seen.add(id);
		out.push(id);
	});
	return out;
}

/** What changed from `before` to `after`: `{ add, remove }`. */
export function tagDelta(before = [], after = []) {
	const was = new Set(idList(before));
	const now = new Set(idList(after));
	return {
		add: [...now].filter(id => !was.has(id)),
		remove: [...was].filter(id => !now.has(id)),
	};
}

export const sameTagIds = (a = [], b = []) => {
	const x = idList(a);
	const y = idList(b);
	return x.length === y.length && x.every((id, index) => id === y[index]);
};

/** The item's private applications with `delta` applied (an optimistic `myTags`). */
export function withPrivateDelta(myTags = [], { add = [], remove = [] } = {}) {
	const drop = new Set((remove || []).map(entry => normalizeId(entry?.tagId ?? entry)));
	const kept = (Array.isArray(myTags) ? myTags : []).filter(entry => entry?.fromCollection || !drop.has(normalizeId(entry?.tagId)));
	const have = new Set(kept.filter(entry => !entry?.fromCollection).map(entry => normalizeId(entry.tagId)));
	(add || []).forEach(entry => {
		const tagId = normalizeId(entry?.tagId);
		if (!tagId || have.has(tagId)) return;
		have.add(tagId);
		kept.push({ tagId, kind: entry.kind === 'item' ? 'item' : 'tag', ...(entry.name ? { name: entry.name } : {}) });
	});
	return kept;
}

/** Private applications that differ between two `myTags` lists: `{ add, remove }` of entries. */
export function privateDelta(before = [], after = []) {
	const was = new Map((Array.isArray(before) ? before : []).filter(entry => !entry?.fromCollection).map(entry => [normalizeId(entry.tagId), entry]));
	const now = new Map((Array.isArray(after) ? after : []).filter(entry => !entry?.fromCollection).map(entry => [normalizeId(entry.tagId), entry]));
	return {
		add: [...now.entries()].filter(([id]) => id && !was.has(id)).map(([, entry]) => entry),
		remove: [...was.entries()].filter(([id]) => id && !now.has(id)).map(([, entry]) => entry),
	};
}

// ------------------------------------------------------------------ refusals

const PRIVATE_FAILURE_WORDS = {
	unknown_tag: 'That private tag isn’t there any more.',
	not_found: 'This item can’t be found, or you can no longer read it.',
	personal_tag_full: `This private tag is on as many items as it can hold (${ITEM_TAG_LIMITS.privateItems.toLocaleString('en-US')}).`,
};

/** The first failure code a tag-applications 409 names. */
export const privateFailureCode = error => {
	const failures = error?.response?.data?.failures;
	if (Array.isArray(failures) && failures.length) return failures[0]?.code || null;
	return error?.response?.data?.code || null;
};

/** One refused private application's code, in words (the selection bar names the row beside it). */
export const privateFailureWords = code => PRIVATE_FAILURE_WORDS[code] || 'This one could not be changed.';

/** A private application's refusal (PUT/DELETE /api/tag-applications) as the sentence a person reads. */
export function privateTagRefusal(error) {
	if (!error?.response) return "We couldn't reach the server. Check your connection and try again.";
	const code = privateFailureCode(error);
	return PRIVATE_FAILURE_WORDS[code] || error.response.data?.message || 'The private tag could not be changed. Please try again.';
}

/**
 * A hand-application write's refusal (PUT /api/items/{id}, a bulk row) as
 * the sentence a person reads. The API's codes (keepr-api § Tags on items):
 * unknown_tag, restricted_tag, rule_owned_tag, too_many_tags, stale_item /
 * stale, plus the item's own (item_locked, attestation).
 */
export function tagWriteRefusal(error) {
	if (!error?.response) return "We couldn't reach the server. Check your connection and try again.";
	const { status } = error.response;
	const data = error.response.data || {};
	switch (data.code) {
		case 'unknown_tag':
			return 'That tag can’t go on this item. It may have been deleted, or it belongs to another collection.';
		case 'restricted_tag':
			return 'Only managers of the collection put a restricted tag on items or take it off.';
		case 'rule_owned_tag':
			return 'A rule applies this tag, so it can’t be put on by hand.';
		case 'too_many_tags':
			return `An item holds at most ${ITEM_TAG_LIMITS.perItem} tags. Take one off first.`;
		case 'stale_item':
		case 'stale':
			return 'Someone changed this item’s tags at the same moment. Try again.';
		case 'invalid_tag_ids':
			return 'Those tags could not be read. Please try again.';
		// A tag a group built from records reads, on a record linked to a
		// person (owner ruling b78, `changed: 'tags'`): the item form's words.
		case 'account_link_needs_access':
			return ACCOUNT_LINK_FIELD_WORDS;
		default:
			break;
	}
	if (status === 404) return 'This item can’t be found, or you can no longer read it.';
	if (status === 403 && !data.code) return 'You can’t change this item’s tags.';
	return (typeof data.message === 'string' && data.message) || 'The tags could not be saved. Please try again.';
}

/** Is this a refusal about the tags themselves (the form lands it on the tag row)? */
export const isTagRefusal = error => ['unknown_tag', 'restricted_tag', 'rule_owned_tag', 'too_many_tags', 'invalid_tag_ids']
	.includes(error?.response?.data?.code);

/**
 * A successful write's `tag_dropped` warning (a tag deleted — or taken over
 * by a strict rule, or an item tag turned off — since the form was opened:
 * left off, the rest saved) as one sentence, or '' when there is none.
 */
export function tagDroppedNote(warnings) {
	const dropped = (Array.isArray(warnings) ? warnings : []).filter(warning => warning?.code === 'tag_dropped');
	if (!dropped.length) return '';
	const count = dropped.reduce((sum, warning) => sum + (Array.isArray(warning.tagIds) ? warning.tagIds.length : 1), 0);
	return count === 1
		? 'One tag was deleted since this was opened, so it was left off. Everything else saved.'
		: `${count} tags were deleted since this was opened, so they were left off. Everything else saved.`;
}

// ------------------------------------------------------------------ templates (T9)

/**
 * The caller's OWN private tags on an item (its `myTags`, not the ones its
 * collection carries), as ids — what a draft remembers and a duplicate copies.
 */
export const ownPrivateTagIdsOf = item => (Array.isArray(item?.myTags) ? item.myTags : [])
	.filter(entry => entry && !entry.fromCollection && entry.kind !== 'item')
	.map(entry => normalizeId(entry.tagId))
	.filter(Boolean);

const LAUNCH_DROP_WORDS = {
	restricted: name => `“${name}” (only managers of its collection put it on)`,
	rule: name => `“${name}” (a rule applies it)`,
	deleted: () => 'a tag that was deleted',
	unavailable: () => 'a tag you can’t use here',
};

/**
 * A TEMPLATE NEVER APPLIES A TAG THE PERSON CAN'T SEE LAND (docs/plans/tags.md
 * §5.2, T9). What a quick add, a duplicate, a draft or a multi add step left
 * off — keepr-api's launch `dropped` (`{ tagId, reason, name? }`) — as the one
 * note the form shows beside its tags: "2 tags from the quick add were left
 * off: “Board” (only managers of its collection put it on); a tag that was
 * deleted." Null when nothing was left off. A name only where the API gave one
 * (it names nothing the person cannot read).
 */
export function launchTagsNote(dropped = [], source = 'the quick add') {
	const list = Array.isArray(dropped) ? dropped.filter(Boolean) : [];
	if (!list.length) return null;
	const parts = list.map(entry => (LAUNCH_DROP_WORDS[entry.reason] || LAUNCH_DROP_WORDS.unavailable)(entry.name || 'a tag'));
	return `${list.length === 1 ? 'A tag' : `${list.length} tags`} from ${source} ${list.length === 1 ? 'was' : 'were'} left off: ${parts.join('; ')}.`;
}

/**
 * A launch's tags put on a form's item (`{ tagIds, myTags }` over what it
 * holds): the hand tags added to its `tagIds`, the caller's own private tags
 * to its `myTags` — both as the launch judged them, nothing else.
 */
export function withLaunchTags(item, launch) {
	const hand = Array.isArray(launch?.tagIds) ? launch.tagIds : [];
	const own = Array.isArray(launch?.privateTagIds) ? launch.privateTagIds : [];
	return {
		tagIds: applyTagDelta(handTagIdsOf(item), { add: hand }),
		myTags: withPrivateDelta(item?.myTags, { add: own.map(tagId => ({ tagId, kind: 'tag' })) }),
	};
}
